<template>
    <div class="min-h-screen">
        <div
            v-if="isLoading"
            class="loader relative flex flex-col items-center justify-center m-auto w-20 min-h-screen"
        >
            <loading v-if="!isIppo" class="mb-10" />
        </div>
        <div v-else class="flex flex-col">
            <public-nav v-if="!hasNewHeader" v-once />
            <router-view />
            <app-footer v-once />
            <custom-message-modal />
            <unavailable-categories-modal />
        </div>
        <catering-fulfillment-method-modal
            v-if="hasCateringFulfillmentMethodModal"
        />
        <fulfillment-method-modal />
        <age-verification-modal />
        <table-selection-modal />
        <item-unavailable-modal />
        <offer-unavailable-modal />
        <calculation-method-modal />
        <mandatory-tip-modal />
        <applying-discount-modal
            :details="discountDetails"
            :status="discountAppliedStatus"
        />
        <loyalty-contact-verification-modal />
    </div>
</template>

<script>
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex';
import PublicNav from '@/components/PublicNav';
import AppFooter from '@/components/AppFooter';
import FulfillmentMethodModal from '@/components/modals/FulfillmentMethodModal';
import ageVerificationModal from '@/components/modals/ageVerificationModal';
import TableSelectionModal from '@/components/modals/TableSelectionModal';
import CustomMessageModal from '@/components/modals/CustomMessageModal';
import ItemUnavailableModal from '@/components/modals/ItemUnavailableModal';
import UnavailableCategoriesModal from '@/components/modals/UnavailableCategoriesModal';
import switchToLangOrCloserLang from '@/services/switchToLangOrCloserLang';
import { FulfillmentMethodsEnum, HeaderStylesEnum } from '@/enums';
import MandatoryTipModal from '@/components/modals/MandatoryTipModal';
import Loading from '@/components/icons/LoadingIcon';
import ApplyingDiscountModal from '../components/modals/ApplyingDiscountModal.vue';
import OfferUnavailableModal from '@/components/modals/OfferUnavailableModal';
import CalculationMethodModal from '@/components/modals/CalculationMethodModal';
import Vue from 'vue';
import CateringFulfillmentMethodModal from '@/components/modals/CateringFulfillmentMethodModal.vue';
import isLocalStorageAvailable from '@/helpers/isLocalStorageAvailable';
import LoyaltyContactVerificationModal from '@/components/modals/LoyaltyContactVerificationModal';
const baoCss = () => import('@/assets/css/bao.css');

export default {
    name: 'PublicRouteContainer',
    head() {
        const slug = this.slug || this.$route.params.venueSlug;

        return {
            // if no subcomponents specify a head.title, this title will be used
            title: this.$t('meta.orderOnline'),
            // all titles will be injected into this template
            titleTemplate: () => this.$store.state.venue.venue.name + ' | %s',
            meta: [
                {
                    name: 'theme-color',
                    content: () => this.$store.state.venue.venue.primaryColour
                },
                {
                    key: 'description',
                    name: 'description',
                    content: () => this.metaDescription
                }
            ],
            link: [
                {
                    rel: 'canonical',
                    href: `https://${this.domain}/${slug}/menu`
                }
            ]
        };
    },
    components: {
        CalculationMethodModal,
        PublicNav,
        AppFooter,
        FulfillmentMethodModal,
        CateringFulfillmentMethodModal,
        ageVerificationModal,
        CustomMessageModal,
        TableSelectionModal,
        ItemUnavailableModal,
        OfferUnavailableModal,
        MandatoryTipModal,
        UnavailableCategoriesModal,
        Loading,
        ApplyingDiscountModal,
        LoyaltyContactVerificationModal
    },
    data() {
        return {
            isLoading: true,
            domain: import.meta.env.VITE_WHITELABEL_DOMAIN,
            FulfillmentMethodsEnum
        };
    },
    async created() {
        try {
            await this.initializeQueryParamModes();
            await this.checkOverrideToken();
            await this.loadVenueData();
            await this.setupLanguage();
            await this.syncAuthFromLocalStorage();

            if (
                this.$store.state.venue.venue.accountId ===
                '5f4e9ddc-3431-43f5-9de0-7aa356564afc'
            ) {
                await baoCss();
            }
            if (this.customerEmail && Vue.prototype.$analytics) {
                Vue.prototype.$analytics.identify(this.customerEmail);
            }
        } catch (e) {
            throw new Error(e);
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        ...mapMutations('cart', ['setFulfillmentMethod']),
        ...mapActions({
            triggerFulfillmentMethodModal:
                'venue/triggerFulfillmentMethodModal',
            triggerCateringFulfillmentMethodModal:
                'venue/triggerCateringFulfillmentMethodModal'
        }),
        async loadVenueData() {
            try {
                await this.$store.dispatch(
                    'venue/getVenue',
                    this.$route.params.venueSlug
                );
                this.triggerModal();
            } catch (error) {
                console.error('Failed to load venue:', error);
                throw error;
            }
        },
        async initializeQueryParamModes() {
            const { storeDesigner, theme, debug } = this.$route.query;
            if (storeDesigner === 'true' || theme) {
                await this.$store.dispatch('helpers/enableStoreDesigner');
            }
            if (debug === 'true') {
                await this.$store.dispatch('helpers/enableDebugMode');
            }
        },
        async checkOverrideToken() {
            const { override } = this.$route.query;
            if (override) {
                await this.$store.dispatch(
                    'venue/validateStaffToken',
                    override
                );
                await this.$router.replace({
                    query: { ...this.$route.query, override: undefined }
                });
            }
        },
        isExcludedRoute() {
            return [
                'orderStatus',
                'Privacy',
                'checkout',
                'orderReview',
                'LandingPage',
                'billStatus'
            ].includes(this.$route.name);
        },
        triggerModal() {
            if (
                !this.isExcludedRoute() &&
                this.$store.state.venue &&
                this.$store.state.venue.venue &&
                !this.$store.state.venue.venue.acceptsInStore
            ) {
                // Check if fulfillment method is provided in query params
                const serviceParam = this.$route.query.service;
                if (serviceParam) {
                    this.handleFulfillmentMethodFromQuery(serviceParam);
                } else if (this.hasCateringFulfillmentMethodModal) {
                    return this.triggerCateringFulfillmentMethodModal();
                } else {
                    this.triggerFulfillmentMethodModal();
                }
            }
        },
        handleFulfillmentMethodFromQuery(serviceParam) {
            // Validate the service parameter
            if (
                !Object.values(FulfillmentMethodsEnum.options).includes(
                    serviceParam
                )
            ) {
                // Invalid service parameter, show modal as usual
                if (this.hasCateringFulfillmentMethodModal) {
                    return this.triggerCateringFulfillmentMethodModal();
                } else {
                    this.triggerFulfillmentMethodModal();
                }
                return;
            }

            // Set the fulfillment method
            this.setFulfillmentMethod(serviceParam);
            this.$store.commit('cart/CLEAR_ZONAL_DISCOUNT');

            // If pickup (regular or catering), close the modal immediately
            if (FulfillmentMethodsEnum.isPickupMethod(serviceParam)) {
                // Don't show the modal at all for pickup
                return;
            }

            // For delivery, show the modal so user can enter address
            if (FulfillmentMethodsEnum.isDeliveryMethod(serviceParam)) {
                if (this.hasCateringFulfillmentMethodModal) {
                    this.triggerCateringFulfillmentMethodModal();
                } else {
                    this.triggerFulfillmentMethodModal();
                }
            }
        },

        async setupLanguage() {
            const venueLang = this.lang;
            const autoLanguage = this.venueValue('autoLanguage');

            let targetLang;

            if (autoLanguage) {
                const browserLang = switchToLangOrCloserLang.findMatchingLang(
                    window.navigator.language
                );
                if (browserLang) {
                    targetLang = browserLang;
                } else {
                    targetLang = venueLang;
                }
            } else {
                targetLang = venueLang;
            }

            if (targetLang) {
                await switchToLangOrCloserLang.handle(
                    targetLang,
                    this.venueId,
                    this.accountId
                );
            }
        },

        syncAuthFromLocalStorage() {
            if (this.accountId && isLocalStorageAvailable()) {
                const skAuthApproval = localStorage.getItem(
                    `sk_auth_${this.accountId}`
                );

                this.$store.commit(
                    'customer/SET_AUTH',
                    skAuthApproval === 'true'
                );
            }
        }
    },
    computed: {
        ...mapGetters({
            forceRefresh: 'helpers/forceRefresh',
            discountAppliedStatus: 'discount/status',
            discountDetails: 'discount/details',
            venueValue: 'venue/venueValue'
        }),
        ...mapState('venue', {
            slug: state => state.venue.slug,
            visualMenu: state => state.venue.visualMenu
        }),
        ...mapGetters('venue', ['isPatEnabled']),
        ...mapState('customer', {
            customerEmail: state => state.email
        }),
        metaDescription() {
            const description =
                this.$store.state.venue.venue.metaDescription ||
                this.$store.state.venue.venue.description;

            return description.slice(0, 157);
        },
        hasNewHeader() {
            return (
                this.isPatEnabled ||
                this.$growthbook.isOn('new_header') ||
                this.venueValue('headerStyle') === HeaderStylesEnum.MINIMAL
            );
        },
        hasCateringFulfillmentMethodModal() {
            if (
                this.$store.state.venue.venue.id === 9133 ||
                this.$store.state.venue.venue.id === 9469
            ) {
                return true;
            }
            return this.$growthbook.isOn('catering_fulfillment_method_modal');
        },
        venueId() {
            return this.venueValue('id');
        },
        lang() {
            return this.venueValue('lang');
        },
        accountId() {
            return this.venueValue('accountId');
        },
        isIppo() {
            return window.location.href.includes('ippo');
        }
    }
};
</script>

<style scoped src="@/assets/css/loading-animation_scoped.css"></style>
